import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor";
import Image from "next/legacy/image";
import Script from "next/script";

export default function HomeGardeningArticle({}) {
  const content = () => {
    return (
      <>
        <div className={styles["Article__image"]}>
          <Image
            src={
              "/static/images/articles/indoor-outdoor-home-gardening--sm.1x.min.webp"
            }
            alt={"Indoor Garden"}
            width={900}
            height={565}
          />
        </div>
        <h1 className={styles["Article__heading"]}>
          Indoor and Outdoor Gardening for Your Home
        </h1>
        <ArticleAuthor />
        <p>
          Gardening has many useful benefits for the environment, as well as
          physical and mental health. The relaxing nature of gardening plus the
          fact that it can be an indoor or outdoor activity, make it a popular
          pastime for many. There are, however, people who don't know how to
          start and grow a garden. Fortunately, when equipped with the right
          information, plants, and tools, most can successfully grow anything
          from houseplants to floral or food gardens right in their home, patio,
          or yard.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Types of Plants</h2>
        <p>
          When planning an indoor or outdoor garden, the first step is to decide
          on what type of plants to grow. Various types of plants include herbs,
          palms, ferns, bamboos, cacti and succulents. They may flower or be
          non-flowering, annual or perennial.
        </p>
        <p>
          Not all plants, however, are suitable for all environments. Some plant
          types cannot successfully thrive in indoor pots, for example. There
          are also plants that are better suited for the controlled climate of
          the indoors or filtered lighting, while others require open-air
          conditions and direct sun.
        </p>

        <h2 className={styles["Article__sub-heading"]}>Tools and Equipment</h2>
        <p>
          In addition to the right type of plant, the right tools are also a
          necessity. Whether gardening indoors or outdoors, one should have a
          pair of comfortable gardening gloves to not only keep their hands
          clean, but to protect them from thorns, scratches, or coming into
          contact with leaves or flowers that may be toxic to the touch. Other
          basic tools that one will need regardless of where they intend to grow
          their plants include pruning shears, a hand trowel, and a hand fork.
        </p>
        <p>
          Indoor gardeners also need pots for planting, a watering can, and a
          plant mister. In homes with limited space, a windowsill potting kit
          can be suitable for flowers or herbs and will ensure they receive
          enough sunlight. A plant stand and a moisture meter are also examples
          of what one may need for their indoor garden.
        </p>
        <p>
          When gardening outdoors, there are a few basic tools needed. This
          includes a garden hoe, a water hose with a nozzle, a shovel or long
          spade, and a garden rake. Depending on the size of the garden and the
          need to haul soil, one may need a wheelbarrow. In areas with hard
          dirt, a cultivator or tiller may be necessary.
        </p>
        <p>
          Patio gardens are most commonly container gardens or bed gardens. For
          bed gardening, a{" "}
          <a href="https://sfyl.ifas.ufl.edu/media/sfylifasufledu/orange/hort-res/docs/pdf/008-How-to-Build-a-Raised-Bed-Garden.pdf">
            raised garden bed
          </a>{" "}
          or a planter box is necessary.
        </p>

        <h2 className={styles["Article__sub-heading"]}>Lighting</h2>
        <p>
          Lighting plays a critical role in growing healthy plants. All plants
          require light, but not to the same degree. Before buying a plant, it
          is important to check the lighting requirements to ensure that it is
          suitable. A plant that requires full sun, for example, will flourish
          outdoors versus being in an apartment with low light.
        </p>
        <p>
          When planting outside, plants that require partial or full shade
          should be appropriately placed in areas that receive shade from the
          sun, such as areas close to walls or in areas that are near trees.
        </p>
        <p>
          People who live in homes or apartments should ideally choose plants
          that require low or indirect light. South and East-facing windows
          often provide the best indirect lighting for plants. People who live
          in houses or apartments that have little to no natural lighting can
          still be successful at indoor gardening.{" "}
          <a href="https://www.apartmenttherapy.com/houseplant-grow-light-guide-36859540">
            Grow lights
          </a>{" "}
          can help meet the needs of some houseplants, even full-light plants
          that require up to 6 to 8 hours of sunlight.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Water</h2>
        <p>
          One area that novice gardeners have difficulty with is how much and
          how often to water their plants. It is important that people follow
          the watering guidelines to ensure their plants do not die from
          dehydration or from too much water.
        </p>
        <p>
          Houseplants rely entirely on the water from the person who cares for
          them. One should learn how to check the soil manually for moisture or
          purchase moisture meters that will show when a plant needs hydration.
          A watering schedule can also help gardeners keep track of which plant
          needs watering and when.
        </p>
        <p>
          Outdoor plants receive moisture from a variety of sources, such as
          rain, mist, or even morning dew. This can make it hard to control how
          much water they're actually receiving. Sprinklers or drip systems use
          timers to maintain a regular schedule and are adjustable for days that
          are naturally wet. A drip system works by placing the water at the
          base of each plant so that it isn't wasted on surrounding dirt.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Size</h2>
        <p>
          Plants come in various sizes. Not all plant sizes, however, are
          suitable for in-home growth. Because the size of its container limits
          a plant's growth, there are certain types of plants that need space
          for their roots to grow.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Climate</h2>
        <p>
          Depending on where a person lives, they may be subject to blistering
          hot summers or chilly winters. Extreme{" "}
          <a href="https://www.womeninagscience.org/post/let-s-grow-a-garden-part-1-temperature-a-key-factor-for-growing-our-food">
            temperature
          </a>{" "}
          can affect plants in various ways, including their ability to thrive.
          A plant that needs full sun may not, for example, be able to endure
          the high heat that comes with summer in certain areas. People who live
          in areas that experience extreme climate conditions will want to
          choose plants that are ideally suited for the weather. This is
          important for outdoor gardens that are more susceptible to the
          elements. Selecting plants that are native to the area is one way to
          ensure that a plant can survive the climate. People can also consult
          the
          <a href="https://planthardiness.ars.usda.gov/">
            USDA Plant Hardiness Zone map
          </a>{" "}
          that helps gardeners determine if a plant can survive locations based
          on minimum cold weather, or by consulting the American Horticultural
          Society (AHS) Plant Heat-Zone Map.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Pests</h2>
        <p>
          Pests plague plant life both inside the home and outside. Indoor
          gardens are typically at risk of damage from{" "}
          <a href="https://hgic.clemson.edu/factsheet/common-houseplant-insects-related-pests/">
            insects
          </a>{" "}
          such as spider mites, scale, and mealybugs. Outdoor pests, however,
          are more varied and can be difficult to control. Besides leaf-eating
          aphids and other types of pests that plague household plants,
          gardeners must fend off wildlife. Deer, squirrels, and{" "}
          <a href="https://hortnews.extension.iastate.edu/2002/6-21-2002/rabbits.html">
            rabbits
          </a>{" "}
          are all examples of pests that can destroy food or even flower
          gardens. To combat this type of pest, fencing, netting and other
          deterrents may be useful.
        </p>
        <ul>
          <li>
            <p>
              <a href="https://nylcvef.org/citizens-toolkit/green-gardening-tips-establishing-your-garden/">
                Green Gardening Tips: Establishing Your Garden
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.environment911.org/The-Ultimate-Guide-to-Purchasing-Indoor-Plants">
                The Ultimate Guide to Purchasing Indoor Plants
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://stanfordmag.org/contents/gardening-with-native-plants-essential-answer">
                Gardening With Native Plants: Essential Answer
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://lnt.org/the-benefits-of-planting-a-native-garden/">
                Benefits of Planting a Native Garden
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.hgtv.com/outdoors/landscaping-and-hardscaping/landscaping-101-learn-the-different-plant-groups-pictures">
                Landscaping 101: Different Types of Plants
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://foodprint.org/growing-your-own-food/">
                Growing Your Own Food
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://extension.unh.edu/blog/2020/08/irrigation-systems-garden">
                Irrigation Systems for Gardens
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://extension.okstate.edu/fact-sheets/print-publications/hla/basic-plant-care-understanding-your-plants-needs-hla-6461.pdf">
                Basic Plant Care: Understanding Your Plant's Needs
              </a>
              (PDF)
            </p>
          </li>
          <li>
            <p>
              <a href="https://anaturalcuriosity.org/how-to-choose-the-right-soil-for-your-new-houseplant/">
                How to Choose the Right Potting Mix for Your New Houseplant
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://extensionpublications.unl.edu/assets/pdf/g837.pdf">
                Guide to Selecting Houseplants
              </a>{" "}
              (PDF)
            </p>
          </li>
          <li>
            <p>
              <a href="https://uwyoextension.org/washakiecounty/wp-content/uploads/2020/04/USU_RaisedBed.pdf">
                Raised Bed Gardening
              </a>
              (PDF)
            </p>
          </li>
          <li>
            <p>
              <a href="https://dbg.org/5-tips-to-keep-your-indoor-plants-healthy/">
                Five Tips to Keep Your Indoor Plants Healthy
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://doitgreen.org/topics/gardening/water-doesnt-have-kill-your-houseplants/">
                Water Doesn't Have to Kill Your Houseplants
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://indoorplantcare.org/water-houseplants/">
                The Right Way to Water Houseplants
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.regionalh2o.org/water-conservation/outdoor-water-conservation/watering-efficiently-outdoors">
                Watering Efficiently Outdoors
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.missouribotanicalgarden.org/Portals/0/Gardening/Gardening%20Help/Factsheets/Containers13.pdf">
                Containers for Growing Plants
              </a>{" "}
              (PDF)
            </p>
          </li>
          <li>
            <p>
              <a href="https://kidsgardening.org/resources/garden-activities-container-gardening-for-kids/">
                Container Gardening for Kids
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://piedmontmastergardeners.org/article/container-gardening-part-i/">
                Container Gardening: Part I
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://www.finegardening.com/article/10-ways-to-keep-your-garden-healthy">
                Ten Ways to Keep Your Garden Healthy
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://ahsgardening.org/about-us/news-press/cool_timeline/heat-zone-map-developed/">
                American Horticultural Society (AHS) Plant Heat-Zone Map
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://today.oregonstate.edu/news/tips-gardening-extreme-heat">
                Tips for Gardening in Extreme Heat
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://extension.psu.edu/planting-in-sun-or-shade">
                Planting in Sun or Shade
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://mgnv.org/houseplants/managing-houseplant-pests/">
                Managing Houseplant Pests
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="https://gardening.org/10-most-common-garden-pests-and-how-to-fight-them/">
                Ten Most Common Garden Pests and How to Fight Them
              </a>
            </p>
          </li>
          <li>
            <p>
              <a href="http://ipm.ucanr.edu/PMG/PESTNOTES/pn7404.html">
                Pests in{" "}
              </a>
              <a href="https://www.realestateagents.com/real-estate-agents/tampa-fl">
                Home
              </a>
              <a href="http://ipm.ucanr.edu/PMG/PESTNOTES/pn7404.html">
                {" "}
                Gardens and Landscapes: Aphids
              </a>
            </p>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "Indoor and Outdoor Gardening for Your Home  | RealEstateAgents.com"
        }
        description={
          "Gain a better understanding of the differences between indoor and outdoor gardens, as well as how to start your own garden!"
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/indoor-outdoor-gardening",
          },
          headline: "Indoor and Outdoor Gardening for Your Home",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
